/**
 * Hook para obtener estadísticas de uso de la empresa actual
 * Compara el uso actual con los límites del plan
 */
import { useState, useEffect, useMemo } from 'react';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { usePlanes } from './usePlanes';
import apiClient from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/api';
import { getTrialConfig } from '@/services/configuracionGlobalService';

// Función para obtener estadísticas reales de la empresa usando el endpoint dedicado /api/estadisticas
const getEmpresaStats = async (empresaId) => {
  try {
    //console.log('📊 Fetching real stats for empresa using /api/estadisticas:', empresaId);
    
    // Usar el endpoint dedicado de estadísticas que hace COUNT queries precisas
    const response = await apiClient.get('/api/estadisticas');
    
    //console.log('📊 Estadisticas API response:', response.data);
    
    if (!response.data?.success || !response.data?.data) {
      throw new Error('Invalid response from estadisticas API');
    }
    
    const stats = response.data.data;
    
    console.log('📊 Final processed stats:', {
      sucursales: stats.sucursales?.actual || 0,
      vehiculos: stats.vehiculos?.actual || 0,
      clientes: stats.clientes?.actual || 0,
      mantenimientos: stats.mantenimientos?.actual || 0
    });

    return {
      sucursales: {
        actual: stats.sucursales?.actual || 0,
        total: stats.sucursales?.total || 0
      },
      vehiculos: {
        actual: stats.vehiculos?.actual || 0,
        total: stats.vehiculos?.total || 0
      },
      mantenimientos: {
        actual: stats.mantenimientos?.actual || 0, // Total de mantenimientos
        total: stats.mantenimientos?.total || 0    // Total de mantenimientos
      },
      clientes: {
        actual: stats.clientes?.actual || 0,
        total: stats.clientes?.total || 0
      },
      periodo: stats.periodo || null
    };
  } catch (error) {
    console.error('❌ Error fetching empresa stats from /api/estadisticas:', error);
    
    // Fallback con datos vacíos en caso de error
    return {
      sucursales: { actual: 0, total: 0 },
      vehiculos: { actual: 0, total: 0 },
      mantenimientos: { actual: 0, total: 0 },
      clientes: { actual: 0, total: 0 }
    };
  }
};

export const useEmpresaStats = () => {
  const { empresaActual, planActual: empresaPlan } = useSimpleEmpresa();
  const { planes, loading: planesLoading } = usePlanes();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trialConfig, setTrialConfig] = useState(null);

  // Obtener límites del plan actual
  const planLimits = useMemo(() => {
    if (!empresaActual?.plan_id || !planes.length) return null;
    
    const currentPlan = planes.find(p => p.id === empresaActual.plan_id);
    if (!currentPlan) return null;
    
    return {
      sucursales: currentPlan.max_sucursales || currentPlan.limites?.sucursales || 1,
      vehiculos: currentPlan.max_vehiculos || currentPlan.limites?.vehiculos || 100,
      mantenimientos: currentPlan.max_mantenimientos_mes || currentPlan.limites?.mantenimientos || 50,
      clientes: currentPlan.max_clientes || currentPlan.limites?.usuarios || 100
    };
  }, [empresaActual?.plan_id, planes]);

  // Cargar configuración de trial
  useEffect(() => {
    const loadTrialConfig = async () => {
      try {
        const config = await getTrialConfig();
        setTrialConfig(config);
      } catch (error) {
        console.error('❌ Error loading trial config:', error);
        // Usar valores por defecto
        setTrialConfig({
          trialDays: 30,
          warningDays: 7,
          graceDays: 7,
          enabled: true
        });
      }
    };

    loadTrialConfig();
  }, []);

  // Cargar estadísticas de la empresa
  useEffect(() => {
    const loadStats = async () => {
      if (!empresaActual?.id) {
        setStats(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const empresaStats = await getEmpresaStats(empresaActual.id);
        setStats(empresaStats);
      } catch (err) {
        console.error('Error loading empresa stats:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [empresaActual?.id]);

  // Calcular métricas de uso
  const usageMetrics = useMemo(() => {
    //console.log('🔢 Calculating usage metrics:', { stats, planLimits });
    
    if (!stats || !planLimits) {
      //console.log('❌ No stats or planLimits available');
      return null;
    }

    const calculateUsage = (actual, max) => {
      // Para límites ilimitados (max = -1), mostrar siempre como saludable
      if (max === -1) {
        //console.log(`📐 Usage calc (unlimited): ${actual}/∞ = 0%`);
        return {
          actual,
          max,
          percentage: 0,
          isNearLimit: false,
          isAtLimit: false,
          remaining: Infinity,
          isUnlimited: true
        };
      }
      
      const percentage = Math.min(Math.round((actual / max) * 100), 100);
      const isNearLimit = percentage >= 80;
      const isAtLimit = actual >= max;
      
      //console.log(`📐 Usage calc: ${actual}/${max} = ${percentage}%`);
      
      return {
        actual,
        max,
        percentage,
        isNearLimit,
        isAtLimit,
        remaining: Math.max(max - actual, 0),
        isUnlimited: false
      };
    };

    const metrics = {
      sucursales: calculateUsage(stats.sucursales.actual, planLimits.sucursales),
      vehiculos: calculateUsage(stats.vehiculos.actual, planLimits.vehiculos),
      mantenimientos: calculateUsage(stats.mantenimientos.actual, planLimits.mantenimientos),
      clientes: calculateUsage(stats.clientes.actual, planLimits.clientes)
    };
    
    //console.log('📊 Final usage metrics:', metrics);
    return metrics;
  }, [stats, planLimits]);

  // Calcular el porcentaje general de uso (métrica más crítica)
  const overallUsage = useMemo(() => {
    if (!usageMetrics) return 0;

    // Priorizar mantenimientos y vehículos como métricas más importantes
    const weights = {
      mantenimientos: 0.4,
      vehiculos: 0.3,
      sucursales: 0.2,
      clientes: 0.1
    };

    const weightedAverage = Object.entries(weights).reduce((acc, [key, weight]) => {
      return acc + (usageMetrics[key]?.percentage || 0) * weight;
    }, 0);

    return Math.round(weightedAverage);
  }, [usageMetrics]);

  // Determinar la métrica más crítica
  const criticalMetric = useMemo(() => {
    if (!usageMetrics) return null;

    const metrics = Object.entries(usageMetrics).map(([key, data]) => ({
      name: key,
      ...data
    }));

    // Encontrar la métrica con mayor porcentaje de uso
    const critical = metrics.reduce((prev, current) => 
      current.percentage > prev.percentage ? current : prev
    );

    return critical.percentage > 0 ? critical : null;
  }, [usageMetrics]);

  // Calcular días restantes del trial para plan gratuito
  const trialInfo = useMemo(() => {
    if (!empresaActual || !trialConfig) return null;

    // Solo para plan gratuito
    const isFreePlan = empresaActual.plan_id === 'revvly_free' || 
                      planes.find(p => p.id === empresaActual.plan_id)?.price === 0;
    
    if (!isFreePlan) return null;

    // Calcular días desde fecha_registro usando configuración
    const fechaRegistro = new Date(empresaActual.fecha_registro);
    const ahora = new Date();
    const diasTranscurridos = Math.floor((ahora - fechaRegistro) / (1000 * 60 * 60 * 24));
    const diasTrial = trialConfig.trialDays;
    const diasAdvertencia = trialConfig.warningDays;
    const diasRestantes = Math.max(diasTrial - diasTranscurridos, 0);
    
    const trialExpirado = diasRestantes === 0;
    const trialProximoAExpirar = diasRestantes <= diasAdvertencia && diasRestantes > 0;

    console.log('🗓️ Trial info calculated:', {
      fechaRegistro: empresaActual.fecha_registro,
      diasTranscurridos,
      diasRestantes,
      diasTrial,
      diasAdvertencia,
      trialExpirado,
      trialProximoAExpirar
    });

    return {
      diasRestantes,
      diasTranscurridos,
      totalDias: diasTrial,
      trialExpirado,
      trialProximoAExpirar,
      porcentajeUsado: Math.round((diasTranscurridos / diasTrial) * 100)
    };
  }, [empresaActual, planes, trialConfig]);

  // Obtener recomendación de upgrade
  const upgradeRecommendation = useMemo(() => {
    if (!usageMetrics || !planes.length) return null;

    // Si el trial está expirado o próximo a expirar, recomendar upgrade
    if (trialInfo?.trialExpirado || trialInfo?.trialProximoAExpirar) {
      const currentPlanIndex = planes.findIndex(p => p.id === empresaActual?.plan_id);
      const nextPlan = planes[currentPlanIndex + 1];
      
      return nextPlan ? {
        recommended: true,
        targetPlan: nextPlan,
        reason: trialInfo.trialExpirado ? 'Trial expirado' : `Trial expira en ${trialInfo.diasRestantes} días`,
        urgent: trialInfo.trialExpirado
      } : null;
    }

    const hasLimitsIssue = Object.values(usageMetrics).some(metric => 
      metric.isAtLimit || metric.isNearLimit
    );

    if (!hasLimitsIssue) return null;

    // Encontrar el siguiente plan disponible
    const currentPlanIndex = planes.findIndex(p => p.id === empresaActual?.plan_id);
    const nextPlan = planes[currentPlanIndex + 1];

    return nextPlan ? {
      recommended: true,
      targetPlan: nextPlan,
      reason: criticalMetric ? `${criticalMetric.name} al ${criticalMetric.percentage}%` : 'Límites próximos'
    } : null;
  }, [usageMetrics, planes, empresaActual?.plan_id, criticalMetric, trialInfo]);

  return {
    stats,
    planLimits,
    usageMetrics,
    overallUsage,
    criticalMetric,
    upgradeRecommendation,
    trialInfo,
    loading: loading || planesLoading,
    error
  };
};

export default useEmpresaStats;